import React, { useState, useEffect, useRef } from 'react';
import { useCms } from '../context/CmsContext';
import {
  ArrowRight,
  Activity,
  FileSearch,
  Calculator,
  Cpu,
  ShieldCheck,
  Wrench,
} from 'lucide-react';

interface ProductsSectionProps {
  onOpenInquiry?: (subject?: string) => void;
  onNavigate?: (page: any) => void;
  onOpenConnect?: () => void;
}

// =========================================================================
// BESPOKE ULTRA-SLEEK 1PX CLEAN PRODUCT ICONS (1PX STROKE, MINIMALIST)
// =========================================================================

// 1. Electrical Drives: VFD & Servo (Sleek 1px Clean Drive Chassis & AC Wave)
const SleekVfdServoIcon: React.FC<{ className?: string }> = ({ className = 'w-8 h-8' }) => (
  <svg
    viewBox="0 0 32 32"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={`${className} transition-transform duration-300 group-hover:scale-105`}
    stroke="currentColor"
    strokeWidth="1"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="5" y="4" width="22" height="24" rx="2" />
    <path d="M10 16c2.5-4 3.5-4 6 0s3.5 4 6 0" />
    <line x1="16" y1="4" x2="16" y2="7.5" />
    <line x1="16" y1="24.5" x2="16" y2="28" />
  </svg>
);

// 2. DC Drives & Converters (Sleek 1px Clean Speed Tachometer Gauge)
const SleekDcDriveIcon: React.FC<{ className?: string }> = ({ className = 'w-8 h-8' }) => (
  <svg
    viewBox="0 0 32 32"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={`${className} transition-transform duration-300 group-hover:scale-105`}
    stroke="currentColor"
    strokeWidth="1"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M7 21a10 10 0 1 1 18 0" />
    <line x1="16" y1="16" x2="22" y2="10" />
    <circle cx="16" cy="16" r="1.5" fill="currentColor" stroke="none" />
    <line x1="6" y1="25" x2="26" y2="25" />
  </svg>
);

// 3. SIMATIC PLCs (Sleek 1px Clean Modular Processor Core)
const SleekPlcIcon: React.FC<{ className?: string }> = ({ className = 'w-8 h-8' }) => (
  <svg
    viewBox="0 0 32 32"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={`${className} transition-transform duration-300 group-hover:scale-105`}
    stroke="currentColor"
    strokeWidth="1"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="7" y="7" width="18" height="18" rx="2" />
    <rect x="12" y="12" width="8" height="8" rx="1" />
    <line x1="12" y1="3" x2="12" y2="7" />
    <line x1="20" y1="3" x2="20" y2="7" />
    <line x1="12" y1="25" x2="12" y2="29" />
    <line x1="20" y1="25" x2="20" y2="29" />
    <line x1="3" y1="12" x2="7" y2="12" />
    <line x1="3" y1="20" x2="7" y2="20" />
    <line x1="25" y1="12" x2="29" y2="12" />
    <line x1="25" y1="20" x2="29" y2="20" />
  </svg>
);

// 4. Distributed I/O Stations (Sleek 1px Clean Modular Slices & Rail)
const SleekDistributedIoIcon: React.FC<{ className?: string }> = ({ className = 'w-8 h-8' }) => (
  <svg
    viewBox="0 0 32 32"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={`${className} transition-transform duration-300 group-hover:scale-105`}
    stroke="currentColor"
    strokeWidth="1"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="3" y1="25" x2="29" y2="25" />
    <rect x="6" y="5" width="5.5" height="20" rx="1" />
    <rect x="13.25" y="5" width="5.5" height="20" rx="1" />
    <rect x="20.5" y="5" width="5.5" height="20" rx="1" />
    <line x1="8.75" y1="9" x2="8.75" y2="12" />
    <line x1="16" y1="9" x2="16" y2="12" />
    <line x1="23.25" y1="9" x2="23.25" y2="12" />
  </svg>
);

// 5. HMI Touch Panels (Sleek 1px Clean Operator Display)
const SleekHmiIcon: React.FC<{ className?: string }> = ({ className = 'w-8 h-8' }) => (
  <svg
    viewBox="0 0 32 32"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={`${className} transition-transform duration-300 group-hover:scale-105`}
    stroke="currentColor"
    strokeWidth="1"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="4" y="5" width="24" height="18" rx="2" />
    <path d="M8 15l4.5-4 4 3.5 7.5-6" />
    <line x1="11" y1="27" x2="21" y2="27" />
    <line x1="16" y1="23" x2="16" y2="27" />
  </svg>
);

// 6. SCADA Systems & Motors (Sleek 1px Clean Motor & Fieldbus Node)
const SleekScadaMotorsIcon: React.FC<{ className?: string }> = ({ className = 'w-8 h-8' }) => (
  <svg
    viewBox="0 0 32 32"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={`${className} transition-transform duration-300 group-hover:scale-105`}
    stroke="currentColor"
    strokeWidth="1"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="14" cy="16" r="8.5" />
    <circle cx="14" cy="16" r="3" />
    <line x1="22.5" y1="16" x2="28" y2="16" />
    <line x1="14" y1="4" x2="14" y2="7.5" />
    <line x1="14" y1="24.5" x2="14" y2="28" />
    <line x1="26" y1="13" x2="28" y2="16" />
    <line x1="26" y1="19" x2="28" y2="16" />
  </svg>
);

interface ProductItem {
  id: string;
  title: string;
  description: string;
  inquirySubject: string;
  icon: React.FC<{ className?: string }>;
}

const SIX_PRODUCTS: ProductItem[] = [
  {
    id: 'electrical-drives',
    title: 'Electrical Drives: VFD & Servo',
    description:
      'High-efficiency Siemens SINAMICS V20, G120, and S120 multi-axis servo systems engineered for microsecond dancer loop feedback and precision speed control.',
    inquirySubject: 'Siemens SINAMICS Electrical Drives & Servo Solutions',
    icon: SleekVfdServoIcon,
  },
  {
    id: 'dc-drives',
    title: 'DC Drives & Converters',
    description:
      'High-power Siemens SINAMICS 6RA80 digital DC converters engineered for heavy rolling mills, extruders, and direct legacy 6RA24/70 DC retrofits.',
    inquirySubject: 'Siemens SINAMICS 6RA80 DC Drives & Retrofits',
    icon: SleekDcDriveIcon,
  },
  {
    id: 'simatic-plcs',
    title: 'SIMATIC PLCs',
    description:
      'Modular SIMATIC S7-1500 & S7-1200 advanced controllers with integrated fail-safe safety for deterministic high-speed line orchestration.',
    inquirySubject: 'Siemens SIMATIC S7-1500 & S7-1200 Automation Systems',
    icon: SleekPlcIcon,
  },
  {
    id: 'distributed-io',
    title: 'Distributed I/O Stations',
    description:
      'PROFINET SIMATIC ET 200SP and ET 200MP remote distributed I/O nodes delivering millisecond fieldbus response and hot-swappable modularity.',
    inquirySubject: 'Siemens SIMATIC ET 200SP / MP Distributed I/O',
    icon: SleekDistributedIoIcon,
  },
  {
    id: 'hmi',
    title: 'HMI Touch Panels',
    description: 'High-resolution SIMATIC Comfort & Unified operator touch panels designed for intuitive plant visualization, alarming, and recipe management.',
    inquirySubject: 'Siemens SIMATIC HMI Comfort & Unified Panels',
    icon: SleekHmiIcon,
  },
  {
    id: 'scada',
    title: 'SCADA Systems & Motors',
    description: 'Enterprise WinCC SCADA architectures with 21 CFR Part 11 recipe audit compliance paired with high-efficiency Siemens industrial AC & servo motors.',
    inquirySubject: 'Siemens WinCC SCADA & Industrial Motor Solutions',
    icon: SleekScadaMotorsIcon,
  },
];

// =========================================================================
// SDLC PROJECT LIFECYCLE DATA (5 CLEAN STAGES WITH VISIBLE ICONS)
// =========================================================================
interface SdlcStage {
  id: string;
  stepNum: string;
  title: string;
  shortTitle: string;
  icon: React.FC<{ className?: string }>;
  tagline: string;
  description: string;
  deliverables: string[];
  color: string;
  accentBg: string;
}

const SDLC_STAGES: SdlcStage[] = [
  {
    id: 'requirement',
    stepNum: '01',
    title: 'Requirement & Analysis',
    shortTitle: 'Requirement & Analysis',
    icon: FileSearch,
    tagline: 'Deep Plant Audit & Feasibility',
    description: 'Comprehensive plant survey, machine cycle time study, field I/O count estimation, and Siemens automation feasibility architecture.',
    deliverables: ['System Architecture Blueprint', 'I/O Schedule & Bill of Materials', 'Safety & Interlock Matrix'],
    color: '#1b7481',
    accentBg: '#eef7f9',
  },
  {
    id: 'budgeting',
    stepNum: '02',
    title: 'Budgeting',
    shortTitle: 'Budgeting',
    icon: Calculator,
    tagline: 'Transparent Capex & ROI Sizing',
    description: 'Competitive OEM-direct pricing for Siemens SINAMICS drives, SIMATIC PLCs, and custom control panels with guaranteed lifecycle cost efficiency.',
    deliverables: ['Transparent Hardware Quotation', 'ROI & Energy Saving Audit', 'Milestone-Based Payment Schedule'],
    color: '#0d6b78',
    accentBg: '#e8f4f6',
  },
  {
    id: 'design-dev',
    stepNum: '03',
    title: 'Design & Development',
    shortTitle: 'Design & Development',
    icon: Cpu,
    tagline: 'CAD Engineering & TIA Portal Logic',
    description: 'EPLAN electrical schematics, 3D panel GA drawings, deterministic S7-1500 PLC ladder logic, and WinCC Unified SCADA interface programming.',
    deliverables: ['EPLAN Electrical Schematics', 'TIA Portal Logic Codebase', 'Dynamic SCADA & HMI Screens'],
    color: '#1b7481',
    accentBg: '#eef7f9',
  },
  {
    id: 'testing',
    stepNum: '04',
    title: 'Testing & QA',
    shortTitle: 'Testing',
    icon: ShieldCheck,
    tagline: 'Rigorous FAT / SAT & Simulation',
    description: 'Simulation-driven digital twin dry runs, insulation & high-voltage testing, Factory Acceptance Testing (FAT), and on-site commissioning.',
    deliverables: ['Factory Acceptance Test (FAT)', 'Site Commissioning Protocol', 'Calibration & Safety Sign-off'],
    color: '#0e7c8c',
    accentBg: '#e8f6f8',
  },
  {
    id: 'amc',
    stepNum: '05',
    title: 'AMC & Lifecycle Support',
    shortTitle: 'AMC & Support',
    icon: Wrench,
    tagline: '24/7 Maintenance & Genuine Spares',
    description: 'Comprehensive Annual Maintenance Contracts (AMC), emergency breakdown response within 4 hours, remote telemetry support, and OEM spare parts.',
    deliverables: ['Quarterly Preventive Health Checks', '24/7 Breakdown Assistance', 'Guaranteed Siemens Spares Inventory'],
    color: '#1b7481',
    accentBg: '#eef7f9',
  },
];

const ProductCardMedia: React.FC<{
  image?: string;
  imageWidth?: string;
  imageHeight?: string;
  imageFit?: string;
  title: string;
  fallbackIcon: React.ComponentType<{ className?: string }>;
}> = ({ image, imageWidth, imageHeight, imageFit, title, fallbackIcon: IconComponent }) => {
  const [hasError, setHasError] = useState(false);

  if (image && !hasError) {
    return (
      <div className="w-12 h-12 shrink-0 overflow-hidden bg-slate-50 border border-slate-200/80 flex items-center justify-center p-1">
        <img
          src={image}
          alt={title}
          loading="lazy"
          decoding="async"
          onError={() => setHasError(true)}
          className="max-w-full max-h-full object-contain"
          style={{
            width: imageWidth && !imageWidth.includes('px') ? imageWidth : '100%',
            height: imageHeight && !imageHeight.includes('px') ? imageHeight : '100%',
            objectFit: (imageFit as any) || 'contain',
          }}
        />
      </div>
    );
  }

  return (
    <div className="text-[#1b7481] group-hover:text-[#06242a] shrink-0 transition-colors">
      <IconComponent className="w-9 h-9 sm:w-10 sm:h-10" />
    </div>
  );
};

export const ProductsSection: React.FC<ProductsSectionProps> = ({
  onOpenInquiry,
  onNavigate,
  onOpenConnect,
}) => {
  const { siteData } = useCms();
  const homeData = siteData?.pages?.home;
  const sectionBgColor = homeData?.productsSectionBgColor || '#ebedf4';
  const sectionBadge = homeData?.productsBadge || homeData?.productsSectionBadge || 'AUTOMATION LIFECYCLE WORKFLOW';
  const sectionHeading = homeData?.productsHeading || homeData?.productsSectionHeading || 'Turnkey engineering and delivery workflow';
  const sectionSubheading = homeData?.productsSubheading || homeData?.productsSectionSubheading || '';
  const sectionCtaText = homeData?.productsCtaText || 'CONNECT';

  // Dynamic typography overrides
  const headingFontFamily = homeData?.productsHeadingFontFamily || "'Jost', sans-serif";
  const headingFontSize = homeData?.productsHeadingFontSize;
  const headingFontWeight = homeData?.productsHeadingFontWeight || '300';
  const headingColor = homeData?.productsHeadingColor || '#06242a';

  const subheadingFontFamily = homeData?.productsSubheadingFontFamily || "'Google Sans Flex', sans-serif";
  const subheadingFontSize = homeData?.productsSubheadingFontSize;
  const subheadingColor = homeData?.productsSubheadingColor || '#4e6569';

  const ctaFontFamily = homeData?.productsCtaFontFamily || 'monospace';
  const ctaFontSize = homeData?.productsCtaFontSize;
  const ctaColor = homeData?.productsCtaColor || '#ffffff';
  const ctaBgColor = homeData?.productsCtaBgColor || '#1b7481';

  const stageFontFamily = homeData?.productsStageFontFamily || "'Google Sans Flex', sans-serif";
  const stageFontSize = homeData?.productsStageFontSize;
  const stageColor = homeData?.productsStageColor || '#06242a';

  const telemetryTitle = homeData?.productsTelemetryTitle || 'COMMISSIONED DRIVES';
  const telemetryValue = homeData?.productsTelemetryValue || '268+ Factory Lines';

  const cardTitleFontFamily = homeData?.productsCardTitleFontFamily || "'Jost', sans-serif";
  const cardTitleFontSize = homeData?.productsCardTitleFontSize;
  const cardTitleColor = homeData?.productsCardTitleColor || '#06242a';
  const cardDescColor = homeData?.productsCardDescColor || '#475569';
  const cardBtnText = homeData?.productsCardBtnText || 'Know more';

  const [activeStageIdx, setActiveStageIdx] = useState<number>(0);
  const [isPaused, setIsPaused] = useState<boolean>(false);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  // Sourced directly from CMS products catalog (without resurrecting deleted products)
  const productsToDisplay = (() => {
    if (Array.isArray(siteData?.products)) {
      if (siteData.products.length === 0) return [];
      return siteData.products
        .filter((p) => p.title && p.title.trim().length > 1 && p.title.trim().toLowerCase() !== 'xcx')
        .map((p, idx) => {
          const fallbackItem = SIX_PRODUCTS.find(
            (sp) => sp.id === p.id || sp.title.toLowerCase().includes(p.title.toLowerCase())
          ) || SIX_PRODUCTS[idx % SIX_PRODUCTS.length];

          return {
            id: p.id || fallbackItem.id,
            title: p.title,
            description: p.subtitle && p.subtitle.trim().length > 15 ? p.subtitle : (p.editorialSubtitle || fallbackItem.description),
            inquirySubject: `Siemens ${p.title} Solutions`,
            image: p.image,
            imageWidth: p.imageWidth,
            imageHeight: p.imageHeight,
            imageFit: p.imageFit,
            icon: fallbackItem.icon,
          };
        });
    }

    return [];
  })();

  // Auto-progress through the 5 SDLC stages every 4 seconds
  useEffect(() => {
    if (isPaused) return;
    const timer = setInterval(() => {
      setActiveStageIdx((prev) => (prev + 1) % SDLC_STAGES.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [isPaused]);

  const currentStage = SDLC_STAGES[activeStageIdx];

  const handleInquiry = (subject: string) => {
    if (onOpenInquiry) {
      onOpenInquiry(subject);
    } else {
      window.location.hash = '#contact';
    }
  };

  const handleCtaClick = () => {
    const target = homeData?.productsCtaTarget || 'modal:inquiry';
    if (target.startsWith('modal:inquiry')) {
      handleInquiry(`Siemens Automation SDLC - ${currentStage.title}`);
    } else if (target.startsWith('modal:connect')) {
      if (onOpenConnect) onOpenConnect();
      else handleInquiry(`Siemens Automation SDLC - ${currentStage.title}`);
    } else if (target.startsWith('page:')) {
      const pageName = target.replace('page:', '');
      if (onNavigate) onNavigate(pageName);
      else window.location.hash = `#${pageName}`;
    } else if (target.startsWith('section:') || target.startsWith('#')) {
      const secId = target.replace('section:', '').replace('#', '');
      const el = document.getElementById(secId);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
      else if (onNavigate) onNavigate('home');
    } else {
      if (onNavigate) onNavigate('product');
      else handleInquiry(`Siemens Automation SDLC - ${currentStage.title}`);
    }
  };

  return (
    <section
      id="products"
      ref={sectionRef}
      className="py-16 sm:py-20 lg:py-24 border-t border-[#1b6f7e]/15 relative overflow-hidden"
      style={{ backgroundColor: sectionBgColor }}
    >
      
      {/* Dynamic Keyframe Animations for Smooth Clean Rotation */}
      <style>{`
        @keyframes cleanRotateClockwise {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes cleanPulseDot {
          0%, 100% { transform: scale(1); opacity: 0.8; }
          50% { transform: scale(1.4); opacity: 1; }
        }
        .anim-clean-track {
          animation: cleanRotateClockwise 60s linear infinite;
        }
      `}</style>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* ========================================================================= */}
        {/* TOP SECTION: SPLIT HEADLINE + CLEAN 5-STAGE SDLC CIRCULAR ANIMATION       */}
        {/* ========================================================================= */}
        <div
          className={`grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-14 items-center mb-12 sm:mb-14 lg:mb-16 transition-all duration-700 ease-out ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          
          {/* LEFT COLUMN: HEADLINE, COPY & CONNECT CTA */}
          <div className="lg:col-span-6 flex flex-col items-start text-left">
            
            {/* Top Eyebrow / Category Label */}
            <div className="text-xs font-mono uppercase tracking-widest text-[#1b7481] font-semibold mb-3 sm:mb-3.5">
              {sectionBadge}
            </div>

            <h2
              className="text-lg sm:text-[22px] md:text-[27px] lg:text-[32px] font-light tracking-tight leading-[1.25] mb-3.5 sm:mb-4"
              style={{
                fontFamily: headingFontFamily,
                fontSize: headingFontSize || undefined,
                fontWeight: headingFontWeight,
                color: headingColor,
              }}
            >
              <span dangerouslySetInnerHTML={{ __html: sectionHeading }} />
            </h2>
            <div className="h-[1px] w-16 bg-gradient-to-r from-[#1b7481] to-[#38c9de] mb-4" />

            <p
              className="text-[15px] font-light leading-relaxed max-w-xl mb-6 sm:mb-8"
              style={{
                fontFamily: subheadingFontFamily,
                fontSize: subheadingFontSize || undefined,
                color: subheadingColor,
              }}
            >
              {sectionSubheading || 'From initial system architecture and PLC programming to custom panel assembly, precision FAT validation, and turnkey on-site commissioning.'}
            </p>

            {/* Single CTA Button */}
            <div>
              <button
                type="button"
                onClick={handleCtaClick}
                className="px-8 py-3.5 text-xs uppercase tracking-widest font-medium transition-all duration-300 shadow-sm hover:shadow-md hover:scale-[1.02] cursor-pointer rounded-none inline-flex items-center gap-2.5"
                style={{
                  fontFamily: ctaFontFamily || 'monospace',
                  fontSize: ctaFontSize || undefined,
                  color: ctaColor,
                  backgroundColor: ctaBgColor,
                }}
              >
                <span>{sectionCtaText}</span>
                <ArrowRight className="w-3.5 h-3.5 text-[#38c9de]" />
              </button>
            </div>
          </div>

          {/* RIGHT COLUMN: CLEAN 5-STAGE SDLC CIRCULAR ANIMATION WITH VISIBLE ICONS */}
          <div
            className="lg:col-span-6 flex flex-col items-center justify-center relative py-6 sm:py-8"
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
          >
            {/* Soft Ambient Radial Backdrop */}
            <div className="absolute w-[360px] h-[360px] sm:w-[440px] sm:h-[440px] rounded-full bg-gradient-to-br from-[#1b7481]/10 via-[#38c9de]/10 to-transparent blur-2xl pointer-events-none" />

            {/* MAIN CIRCULAR CONTAINER */}
            <div className="relative w-[340px] h-[340px] sm:w-[440px] sm:h-[440px] rounded-full flex items-center justify-center select-none">
              
              {/* 1. OUTER CIRCULAR TRACK */}
              <div className="absolute inset-1 sm:inset-3 rounded-full border border-slate-300/80 shadow-sm pointer-events-none" />

              {/* 2. ROTATING DASHED ACCENT RING */}
              <div className="absolute inset-5 sm:inset-8 rounded-full border border-dashed border-[#1b7481]/25 anim-clean-track pointer-events-none">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-[#38c9de]" />
                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 w-1.5 h-1.5 rounded-full bg-[#1b7481]" />
              </div>

              {/* 3. 5 SDLC STAGES: 1-PIECE CLEAN ICONS (NO NUMBERING, NO OUTER TEXT) */}
              {SDLC_STAGES.map((st, idx) => {
                const total = SDLC_STAGES.length;
                // Start from top (-90 deg) and step by 72 deg
                const angle = (idx * (360 / total) - 90) * (Math.PI / 180);
                const radius = 152; // Distance from center
                const isSelected = activeStageIdx === idx;
                const StageIcon = st.icon;

                const x = Math.cos(angle) * radius;
                const y = Math.sin(angle) * radius;

                return (
                  <button
                    key={st.id}
                    type="button"
                    onClick={() => setActiveStageIdx(idx)}
                    style={{
                      transform: `translate(${x}px, ${y}px)`,
                    }}
                    className={`absolute z-20 flex items-center justify-center transition-all duration-300 cursor-pointer ${
                      isSelected ? 'scale-115 z-30' : 'hover:scale-110 opacity-85 hover:opacity-100'
                    }`}
                    aria-label={`Select SDLC stage: ${st.title}`}
                  >
                    {/* Clean 1-Piece Icon Badge (No Numbering, 1px/1.5px stroke icon) */}
                    <div
                      className={`w-12 h-12 sm:w-14 sm:h-14 rounded-none flex items-center justify-center transition-all duration-300 shadow-sm ${
                        isSelected
                          ? 'bg-[#06242a] text-[#38c9de] ring-4 ring-[#1b7481]/30 shadow-[0_6px_20px_rgba(27,116,129,0.3)]'
                          : 'bg-white text-[#1b7481] border border-slate-300/90 hover:border-[#1b7481] hover:text-[#06242a] hover:bg-slate-50'
                      }`}
                    >
                      <StageIcon className="w-5 h-5 sm:w-6 sm:h-6 stroke-[1.5]" />
                    </div>
                  </button>
                );
              })}

              {/* 4. CENTRAL DISPLAY CORE: BOLD CLEAN HEADER NAME ONLY */}
              <div className="relative z-10 w-[200px] h-[200px] sm:w-[245px] sm:h-[245px] rounded-full bg-white border border-slate-200 shadow-[0_12px_36px_rgba(6,36,42,0.08)] p-6 flex items-center justify-center text-center transition-all duration-500">
                
                {/* Large Clean Stage Header Name */}
                <h3
                  className="text-xl sm:text-2xl lg:text-[26px] font-semibold tracking-tight leading-snug px-3 max-w-[210px] mx-auto transition-all duration-300"
                  style={{
                    fontFamily: stageFontFamily,
                    fontSize: stageFontSize || undefined,
                    color: stageColor,
                  }}
                >
                  {currentStage.title}
                </h3>

              </div>

              {/* 5. FLOATING LIVE TELEMETRY BADGE */}
              <div className="absolute -top-3 sm:-top-4 -left-4 sm:-left-8 bg-white rounded-none border border-slate-200 shadow-[0_8px_24px_rgba(6,36,42,0.10)] p-2.5 sm:p-3 flex items-center gap-2.5 z-30 transition-all duration-300 hover:scale-105">
                <div className="w-7 h-7 bg-[#eef7f9] flex items-center justify-center border border-[#38c9de]/30">
                  <Activity className="w-3.5 h-3.5 text-[#1b7481] animate-pulse" />
                </div>
                <div className="text-left">
                  <span className="text-[8px] sm:text-[8.5px] font-mono uppercase text-slate-400 block tracking-wider leading-none">
                    {telemetryTitle}
                  </span>
                  <span className="text-[11px] sm:text-[12px] font-bold text-[#06242a] font-mono leading-tight block mt-0.5">
                    {telemetryValue}
                  </span>
                </div>
              </div>

            </div>

            {/* Bottom Step Indicator Pills */}
            <div className="flex items-center justify-center gap-2 mt-6 z-20">
              {SDLC_STAGES.map((st, idx) => (
                <button
                  key={st.id}
                  type="button"
                  onClick={() => setActiveStageIdx(idx)}
                  className={`h-1.5 rounded-none transition-all duration-300 cursor-pointer ${
                    activeStageIdx === idx
                      ? 'w-6 bg-[#1b7481]'
                      : 'w-1.5 bg-slate-300 hover:bg-slate-400'
                  }`}
                  aria-label={`Show ${st.title}`}
                />
              ))}
            </div>

          </div>
        </div>

        {/* ========================================================================= */}
        {/* BOTTOM SECTION: 6 PRODUCT CARDS (WITH BESPOKE 1PX ANIMATED LINE ICONS)    */}
        {/* ========================================================================= */}
        {productsToDisplay.length === 0 ? (
          <div className="w-full py-12 px-6 bg-white border border-dashed border-slate-300 rounded-none text-center">
            <p className="text-sm font-mono text-slate-500">No products currently published in catalog.</p>
          </div>
        ) : (
          <div
            className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 items-stretch transition-all duration-700 delay-200 ease-out ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            {productsToDisplay.map((item) => {
              const IconComponent = item.icon;

              return (
                <div
                  key={item.id}
                  className="bg-white rounded-2xl border border-slate-200 hover:border-[#1b7481]/50 p-7 sm:p-8 flex flex-col justify-between transition-all duration-300 hover:-translate-y-2 hover:shadow-[0_20px_50px_rgba(27,116,129,0.12)] group"
                >
                  <div>
                    {/* Product Header & Large Ultra-Sleek 1px Animated Vector Icon or Uploaded Image */}
                    <div className="flex items-center gap-3.5 mb-4">
                      <ProductCardMedia
                        image={item.image}
                        imageWidth={item.imageWidth}
                        imageHeight={item.imageHeight}
                        imageFit={item.imageFit}
                        title={item.title}
                        fallbackIcon={IconComponent}
                      />
                      <h3
                        className="text-lg sm:text-xl font-medium tracking-tight"
                        style={{
                          fontFamily: cardTitleFontFamily,
                          fontSize: cardTitleFontSize || undefined,
                          color: cardTitleColor,
                        }}
                      >
                        {item.title}
                      </h3>
                    </div>

                    {/* Concise Description with rich text HTML support */}
                    <div
                      className="text-[13.5px] font-light leading-relaxed mb-6 [&_strong]:font-semibold [&_a]:text-[#1b7481] [&_a]:underline"
                      style={{
                        color: cardDescColor,
                      }}
                      dangerouslySetInnerHTML={{ __html: item.description }}
                    />
                  </div>

                  {/* Bottom Action Link (Know more) */}
                  <button
                    type="button"
                    onClick={() => handleInquiry(item.inquirySubject)}
                    className="inline-flex items-center gap-2 text-xs font-mono font-medium text-[#1b7481] hover:text-[#06242a] group/link transition-colors cursor-pointer pt-4 border-t border-slate-100 text-left w-full justify-between"
                  >
                    <span>{cardBtnText}</span>
                    <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover/link:translate-x-1 text-[#1b7481]" />
                  </button>
                </div>
              );
            })}
          </div>
        )}

      </div>
    </section>
  );
};
